import React from "react";
import { ArrowLeft } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { Chip, ErrorBox, Loading, OBLIGATION_CHIP, useLoad, when } from "@/components/nds/windows/finance/parts";
import { DocAttach, HistoryList, RefLedger } from "@/components/nds/windows/finance/recordParts";
import ObligationActions from "@/components/nds/windows/finance/ObligationActions";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const F = ({ l, v, t }) => <div className="space-y-0.5"><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{l}</div><div data-testid={t} className="text-sm text-nex-silver">{v || "—"}</div></div>;

function DetailsTab({ cfg, rec, reload }) {
  return (
    <div data-testid={`${cfg.testid}-details-tab`} className="p-5 space-y-4">
      <div className="grid grid-cols-3 gap-4 p-4 border border-white/10 rounded-sm">
        <F l="Amount due" v={money(rec.amount_due)} t={`${cfg.testid}-detail-due`} />
        <F l="Paid (from ledger)" v={money(rec.amount_paid)} t={`${cfg.testid}-detail-paid`} />
        <F l="Balance due" v={money(rec.balance_due)} t={`${cfg.testid}-detail-balance`} />
      </div>
      <ObligationActions cfg={cfg} rec={rec} onChanged={reload} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <F l="Resident" v={rec.resident_name} />
        <F l="Period" v={`${rec.period_start} → ${rec.period_end}`} />
        {cfg.feeTypes && <F l="Fee type" v={rec.fee_type?.replace(/_/g, " ")} />}
        <F l="Created" v={`${rec.created_by?.name || "—"} · ${when(rec.created_at)}`} />
        {rec.waived_at && <F l="Waived" v={`${money(rec.waived_amount)} by ${rec.waived_by?.name} · ${rec.waive_reason}`} t={`${cfg.testid}-detail-waive`} />}
        {rec.void_reason && <F l="Void reason" v={rec.void_reason} t={`${cfg.testid}-detail-void`} />}
      </div>
      <DocAttach prefix={cfg.api} rec={rec} onChanged={reload} testid={cfg.testid} />
    </div>
  );
}

export default function ObligationDetail({ cfg, recordId, onBack }) {
  const [tab, setTab] = React.useState("details");
  const [state, load] = useLoad(() => finApi.get(`${cfg.api}/${recordId}`), [recordId]);
  const back = <button type="button" onClick={onBack} data-testid={`${cfg.testid}-back`} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All {cfg.label.toLowerCase()} records</button>;
  if (state.loading && !state.data) return <div className="p-5">{back}<Loading testid={`${cfg.testid}-detail-loading`} /></div>;
  if (state.error) return <div className="p-5">{back}<ErrorBox testid={`${cfg.testid}-detail-error`} error={state.error} onRetry={load} /></div>;
  const rec = state.data;
  const [tone, l] = OBLIGATION_CHIP[rec.status];
  const tabs = [["details", "Details"], ["history", "History"], ["ledger", "Ledger"]];
  return (
    <div data-testid={`${cfg.testid}-detail`} className="flex flex-col h-full">
      <div className="px-5 pt-4 space-y-3 border-b border-white/10">
        {back}
        <div className="flex items-center gap-3"><h3 className="text-lg text-nex-silver font-semibold">{rec.resident_name} · {rec.period_start}</h3><OpenResidentButton residentId={rec.resident_id} name={rec.resident_name} /><Chip tone={tone} data-testid={`${cfg.testid}-detail-status`}>{l}</Chip></div>
        <div className="flex gap-4">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`${cfg.testid}-tab-${id}`} aria-pressed={tab === id}
              className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors ${tab === id ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {tab === "details" && <DetailsTab cfg={cfg} rec={rec} reload={load} />}
        {tab === "history" && <HistoryList history={rec.history} chips={OBLIGATION_CHIP} testid={`${cfg.testid}-history`} />}
        {tab === "ledger" && <RefLedger refType={cfg.refType} refId={rec.id} testid={`${cfg.testid}-ledger`} deps={[rec.status, rec.amount_paid]} />}
      </div>
    </div>
  );
}
